import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

function fmtPrice(q) {
  if (q.kind === "fx") return q.price.toFixed(4);
  if (q.price > 1000) return q.price.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return q.price.toFixed(2);
}
function curSymbol(c) {
  if (c === "GBP") return "£";
  if (c === "EUR") return "€";
  if (c === "JPY") return "¥";
  return "$";
}

function MarketCard({ q }) {
  const up = q.change >= 0;
  const color = up ? "text-emerald-400" : "text-[#FF6B6B]";
  const cs = q.kind === "fx" ? "" : curSymbol(q.currency);
  return (
    <div className="border border-white/15 p-5 hover:border-white/40 transition-colors" data-testid={`home-quote-${q.symbol}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-mono-ed text-[10px] uppercase tracking-[0.22em] text-neutral-500">{q.symbol}</div>
        <div className={`font-mono-ed text-[12px] font-bold ${color}`}>{up ? "▲" : "▼"} {Math.abs(q.change_pct).toFixed(2)}%</div>
      </div>
      <div className="font-serif-ed font-semibold text-lg leading-tight mb-2">{q.label}</div>
      <div className="font-mono-ed text-2xl font-bold text-white">{cs}{fmtPrice(q)}</div>
      <div className={`font-mono-ed text-xs ${color} mt-1`}>{up ? "+" : ""}{q.change.toFixed(q.kind === "fx" ? 4 : 2)} today</div>
    </div>
  );
}

export function HomeMarketsModule() {
  const [quotes, setQuotes] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    api.get("/markets/quotes").then(({ data }) => {
      setQuotes(data.quotes || []);
      setUpdatedAt(data.updated_at);
    }).catch(() => {});
  }, []);

  if (!quotes.length) return null;

  // Show: 3 indices, 2 fx, 1 crypto, 2 stocks
  const pick = (kind, n) => quotes.filter((q) => q.kind === kind).slice(0, n);
  const featured = [
    ...pick("index", 3),
    ...pick("fx", 2),
    ...pick("crypto", 1),
    ...pick("stock", 2),
  ];

  return (
    <section className="bg-[#0B0B0C] text-white px-4 sm:px-6 lg:px-10 py-14 md:py-20 border-y border-white/10 grain" data-testid="home-markets-module">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-10 pb-4 border-b border-white/15">
        <div>
          <div className="overline text-[var(--hinton-red)] mb-2 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--hinton-red)] animate-pulse" /> Hinton Markets · Live
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.04em]">
            Numbers, in motion.
          </h2>
          <p className="mt-3 max-w-xl text-neutral-400 text-base">
            A snapshot of the indices, currencies and equities Hinton readers care about. Refreshed every 60 seconds.
          </p>
        </div>
        <Link
          to="/markets"
          className="inline-flex items-center gap-3 border border-white px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold hover:bg-white hover:text-black transition-colors"
          data-testid="home-markets-cta"
        >
          Open the trading floor <ArrowUpRight size={14} />
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {featured.map((q) => <MarketCard key={q.symbol} q={q} />)}
      </div>

      {updatedAt && (
        <div className="mt-8 text-[11px] font-mono-ed uppercase tracking-[0.22em] text-neutral-500">
          Updated {new Date(updatedAt).toLocaleString("en-GB")} · Quotes via Yahoo Finance
        </div>
      )}
    </section>
  );
}

/**
 * Compact light-theme markets sidebar — designed for the News Money tab.
 * Shows: 3 indices, 4 FX pairs, 4 featured stocks.
 */
function CompactQuote({ q }) {
  const up = q.change >= 0;
  const cs = q.kind === "fx" ? "" : curSymbol(q.currency);
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-black/10 last:border-b-0" data-testid={`money-quote-${q.symbol}`}>
      <div className="min-w-0 flex-1">
        <div className="font-mono-ed text-[10px] uppercase tracking-[0.18em] text-neutral-500">{q.symbol}</div>
        <div className="font-serif-ed text-sm font-semibold truncate">{q.label}</div>
      </div>
      <div className="text-right ml-3 flex-shrink-0">
        <div className="font-mono-ed text-sm font-bold tabular-nums">{cs}{fmtPrice(q)}</div>
        <div className={`font-mono-ed text-[11px] font-bold ${up ? "text-emerald-700" : "text-[var(--hinton-red)]"}`}>
          {up ? "▲" : "▼"} {Math.abs(q.change_pct).toFixed(2)}%
        </div>
      </div>
    </div>
  );
}

export function MoneyMarketsRail() {
  const [quotes, setQuotes] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    const fetchQuotes = () =>
      api.get("/markets/quotes").then(({ data }) => {
        setQuotes(data.quotes || []);
        setUpdatedAt(data.updated_at);
      }).catch(() => {});
    fetchQuotes();
    const id = setInterval(fetchQuotes, 60_000);
    return () => clearInterval(id);
  }, []);

  if (!quotes.length) return null;
  const pick = (kind, n) => quotes.filter((q) => q.kind === kind).slice(0, n);
  const indices = pick("index", 3);
  const fx = pick("fx", 4);
  const stocks = pick("stock", 4);

  return (
    <aside className="space-y-5" data-testid="money-markets-rail">
      {/* Indices */}
      {indices.length > 0 && (
        <div className="border border-black/15 bg-white" data-testid="money-rail-indices">
          <div className="border-b border-black/15 px-5 py-4 flex items-center justify-between">
            <div className="overline text-[var(--hinton-red)]">Indices · Live</div>
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--hinton-red)] animate-pulse" />
          </div>
          <div className="px-5 py-2">
            {indices.map((q) => <CompactQuote key={q.symbol} q={q} />)}
          </div>
        </div>
      )}

      {/* FX */}
      {fx.length > 0 && (
        <div className="border border-black/15 bg-white" data-testid="money-rail-fx">
          <div className="border-b border-black/15 px-5 py-4">
            <div className="overline text-[var(--hinton-red)]">Currency Markets</div>
          </div>
          <div className="px-5 py-2">
            {fx.map((q) => <CompactQuote key={q.symbol} q={q} />)}
          </div>
        </div>
      )}

      {/* Featured equities */}
      {stocks.length > 0 && (
        <div className="border border-black/15 bg-white" data-testid="money-rail-stocks">
          <div className="border-b border-black/15 px-5 py-4">
            <div className="overline text-[var(--hinton-red)]">Featured Equities</div>
          </div>
          <div className="px-5 py-2">
            {stocks.map((q) => <CompactQuote key={q.symbol} q={q} />)}
          </div>
        </div>
      )}

      <Link
        to="/markets"
        className="block border border-black bg-black text-white text-center px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--hinton-red)] hover:border-[var(--hinton-red)] transition-colors"
        data-testid="money-markets-cta"
      >
        Open the trading floor <ArrowUpRight size={12} className="inline ml-1" />
      </Link>

      {updatedAt && (
        <div className="text-[10px] font-mono-ed uppercase tracking-[0.2em] text-neutral-500 text-center">
          Updated {new Date(updatedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} · Yahoo Finance
        </div>
      )}
    </aside>
  );
}
